import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatLabel } from '@angular/material/form-field';
import { MatCheckbox } from '@angular/material/checkbox';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import {
  NewWcgSwitchComponent,
  WcgButtonComponent,
  WcgDialogComponent,
  WcgInputFieldComponent
} from '@wcg/frontend-framework';

import { DocumentTypeForm, DocumentTypesDialogData } from '../../../types';
import { DEFAULT_DOCUMENT_ENTITY_TYPE } from '../../../constants';
import { CONFIGURATION_DOCUMENT_TYPE_ACTIONS } from '../../../actions';
import { mapDocumentFormDataToDocumentType } from '../../../utils';
import { validatePrimaryIRISDocumentId } from '../../../validators';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    ReactiveFormsModule,
    WcgDialogComponent,
    WcgButtonComponent,
    WcgInputFieldComponent,
    NgTemplateOutlet,
    MatLabel,
    MatCheckbox,
    NewWcgSwitchComponent,
  ],
  selector: 'wcg-document-type-dialog',
  styleUrls: ['./document-type-dialog.component.scss'],
  templateUrl: './document-type-dialog.component.html',
})
export class DocumentTypeDialogComponent implements OnInit {
  public readonly data = inject<DocumentTypesDialogData>(MAT_DIALOG_DATA);
  private readonly _dialogRef = inject(
    MatDialogRef<DocumentTypeDialogComponent>,
  );
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _store = inject(Store);
  private readonly _storeActions$ = inject(Actions);

  documentTypeForm = this._createForm();
  submissionTypes = signal<string[]>(['Change in Investigator', 'PRI', 'HUD']); // this will change
  entityTypeOptions = signal(DEFAULT_DOCUMENT_ENTITY_TYPE);
  serverError = signal<string>('');
  primaryIRISDocumentNumericError = signal<string>(
    'Primary IRIS Document ID should be a number',
  );

  ngOnInit(): void {
    // Patching document with dialog data
    if (this.data.data && this.data.editMode) {
      this.documentTypeForm.patchValue(
        { ...this.data.data },
        { emitEvent: false },
      );
    }
    this._customListeners();
  }

  cancel() {
    this._dialogRef.close();
  }

  save() {
    if (!this.data.editMode) {
      this._store.dispatch(
        CONFIGURATION_DOCUMENT_TYPE_ACTIONS.createDocumentType({
          data: mapDocumentFormDataToDocumentType(
            this.documentTypeForm.getRawValue(),
          ),
          wcgTableValues: this.data.wcgTableValues,
        }),
      );
    } else if (this.data.editMode) {
      this._store.dispatch(
        CONFIGURATION_DOCUMENT_TYPE_ACTIONS.updateDocumentType({
          data: mapDocumentFormDataToDocumentType(
            this.documentTypeForm.getRawValue(),
          ),
        }),
      );
    }
  }

  /**
   * @Creating DocumentType Form Group
   * */
  private _createForm(): FormGroup<DocumentTypeForm> {
    return this._formBuilder.group<DocumentTypeForm>({
      additionalIrisDocumentIds: new FormControl<string>('', {
        nonNullable: true,
      }),
      id: new FormControl<string>('', { nonNullable: true }),
      isActive: new FormControl<boolean>(false, { nonNullable: true }),
      name: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      primaryIrisDocumentId: new FormControl<string>('', {
        nonNullable: true,
        validators: [
          Validators.required,
          validatePrimaryIRISDocumentId(/^\d+$/),
        ],
      }),
    });
  }

  private _customListeners(): void {
    /**
     * @Store Action Listeners
     * */
    this._storeActions$
      .pipe(
        ofType(
          ...[
            CONFIGURATION_DOCUMENT_TYPE_ACTIONS.onCreateDocumentTypeSuccess,
            CONFIGURATION_DOCUMENT_TYPE_ACTIONS.onUpdateDocumentTypeSuccess,
          ],
        ),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: () => this._dialogRef.close(),
      });
    this._storeActions$
      .pipe(
        ofType(
          ...[
            CONFIGURATION_DOCUMENT_TYPE_ACTIONS.onCreateDocumentTypeFailure,
            CONFIGURATION_DOCUMENT_TYPE_ACTIONS.onUpdateDocumentTypeFailure,
          ],
        ),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: (error) => {
          const errorMessage = error.error.toString();
          if (errorMessage) {
            this.documentTypeForm.controls.name.setErrors({
              serverError: errorMessage,
            });
            this.serverError.set('Document Type Name must be unique.');
          }
        },
      });
  }
}
